import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  UserCog, Search, MoreVertical, Ban, ShieldCheck, KeyRound, Trash2, Copy, RefreshCcw,
  History, Users2, ExternalLink, AlertTriangle, MessageCircle, Plus, X, Filter, Download,
  FileSpreadsheet, FileText, FileJson, FileType2,
} from "lucide-react";

const ESTADO_BADGE = {
  activo:      { label: "Activo",       cls: "bg-green-100 text-green-800" },
  suspendido:  { label: "Suspendido",   cls: "bg-red-100 text-red-800" },
  pendiente:   { label: "Pre-aprobado", cls: "bg-amber-100 text-amber-800" },
  rechazado:   { label: "Rechazado",    cls: "bg-slate-200 text-slate-700" },
  sin_acceso:  { label: "Sin acceso",   cls: "bg-slate-200 text-slate-700" },
  inactivo:    { label: "Inactivo",     cls: "bg-slate-200 text-slate-700" },
};

const ACCION_META = {
  suspend:        { label: "Suspender",      icon: Ban,         cls: "text-red-700 bg-red-50" },
  reactivate:     { label: "Reactivar",      icon: ShieldCheck, cls: "text-green-700 bg-green-50" },
  reset_password: { label: "Reset password", icon: KeyRound,    cls: "text-orange-700 bg-orange-50" },
  reset_password_temporal: { label: "Reset a Palmaplus", icon: KeyRound, cls: "text-amber-700 bg-amber-50" },
  delete:         { label: "Eliminado",      icon: Trash2,      cls: "text-slate-700 bg-slate-100" },
};

// Campos permitidos para el multi-filtro (deben coincidir con `_MULTIFILTRO_CAMPOS` del backend)
const CAMPOS_FILTRO = [
  { key: "email",        label: "Email",             placeholder: "ej: juan@correo.com" },
  { key: "nombre",       label: "Nombre / Apellido", placeholder: "ej: Pérez" },
  { key: "documento",    label: "RIF / Cédula",      placeholder: "ej: V-12345678" },
  { key: "razon_social", label: "Razón social",      placeholder: "ej: Constructora XYZ" },
  { key: "telefono",     label: "Teléfono",          placeholder: "ej: 04141234567" },
];
const CAMPO_LABELS = Object.fromEntries(CAMPOS_FILTRO.map(c => [c.key, c.label]));

const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("es-VE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Caracas" }); }
  catch { return iso; }
};

export default function UsuariosContribuyentesPage() {
  const { token, API, hasPermission } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const canView    = hasPermission("usuarios_contribuyentes.view");
  const canSuspend = hasPermission("usuarios_contribuyentes.suspend");
  const canReset   = hasPermission("usuarios_contribuyentes.reset_password");
  const canDelete  = hasPermission("usuarios_contribuyentes.delete");
  const canViewLog = hasPermission("usuarios_contribuyentes.view_log");

  const [tab, setTab] = useState("usuarios");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // URL sync para filtros persistentes (shareable / back-forward friendly)
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [estado, setEstado] = useState(() => searchParams.get("estado") || "all");
  const [skip, setSkip] = useState(() => Math.max(0, parseInt(searchParams.get("skip") || "0", 10)));
  const limit = 25;

  // Multi-filtro: lista de {campo, valor}
  const [filtros, setFiltros] = useState(() => {
    const raw = searchParams.getAll("f");
    return raw
      .map((s) => {
        const idx = s.indexOf(":");
        if (idx <= 0) return null;
        const campo = s.slice(0, idx).trim();
        const valor = s.slice(idx + 1).trim();
        if (!valor || !CAMPO_LABELS[campo]) return null;
        return { campo, valor };
      })
      .filter(Boolean);
  });
  // Draft inputs para "Agregar filtro"
  const [draftCampo, setDraftCampo] = useState("email");
  const [draftValor, setDraftValor] = useState("");

  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logFiltroAccion, setLogFiltroAccion] = useState("all");

  const [selected, setSelected] = useState(null);
  const [dlgSuspend, setDlgSuspend] = useState(false);
  const [dlgReset, setDlgReset] = useState(false);
  const [dlgDelete, setDlgDelete] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetInfo, setResetInfo] = useState(null); // {reset_url, expires_hours, email_destino}
  const [exporting, setExporting] = useState(false);

  // Sincroniza el estado actual con la URL (para persistencia + shareable links)
  const syncUrl = (next = {}) => {
    const sp = new URLSearchParams();
    const _q      = next.q       !== undefined ? next.q       : q;
    const _estado = next.estado  !== undefined ? next.estado  : estado;
    const _skip   = next.skip    !== undefined ? next.skip    : skip;
    const _filtros = next.filtros !== undefined ? next.filtros : filtros;
    if (_q) sp.set("q", _q);
    if (_estado && _estado !== "all") sp.set("estado", _estado);
    if (_skip > 0) sp.set("skip", String(_skip));
    (_filtros || []).forEach((f) => sp.append("f", `${f.campo}:${f.valor}`));
    setSearchParams(sp, { replace: true });
  };

  const loadUsers = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (estado && estado !== "all") params.set("estado", estado);
      params.set("skip", skip);
      params.set("limit", limit);
      (filtros || []).forEach((f) => params.append("filtros", `${f.campo}:${f.valor}`));
      const r = await axios.get(`${API}/usuarios-contribuyentes?${params.toString()}`, auth);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar usuarios");
    } finally { setLoading(false); }
  };

  const loadLog = async () => {
    if (!canViewLog) return;
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFiltroAccion && logFiltroAccion !== "all") params.set("accion", logFiltroAccion);
      params.set("limit", 200);
      const r = await axios.get(`${API}/usuarios-contribuyentes/log?${params.toString()}`, auth);
      setLogItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar historial");
    } finally { setLogLoading(false); }
  };

  // Load on mount and when filters change
  useEffect(() => { loadUsers(); syncUrl(); }, [skip, estado, filtros]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "historial") loadLog(); }, [tab, logFiltroAccion]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e?.preventDefault?.(); setSkip(0); loadUsers(); syncUrl({ skip: 0 }); };

  // ============ Multi-filtro ============
  const addFiltro = () => {
    const valor = (draftValor || "").trim();
    if (!valor) { toast.error("Escriba un valor para filtrar."); return; }
    // Evitar duplicados exactos (mismo campo + valor)
    if (filtros.some((f) => f.campo === draftCampo && f.valor.toLowerCase() === valor.toLowerCase())) {
      toast.info("Ese filtro ya está aplicado.");
      return;
    }
    const nextFiltros = [...filtros, { campo: draftCampo, valor }];
    setFiltros(nextFiltros);
    setSkip(0);
    setDraftValor("");
  };
  const removeFiltro = (idx) => {
    const nextFiltros = filtros.filter((_, i) => i !== idx);
    setFiltros(nextFiltros);
    setSkip(0);
  };
  const clearAllFiltros = () => {
    setFiltros([]);
    setQ("");
    setEstado("all");
    setSkip(0);
  };

  // ============ Exportación ============
  const handleExport = async (formato) => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("formato", formato);
      if (q) params.set("q", q);
      if (estado && estado !== "all") params.set("estado", estado);
      (filtros || []).forEach((f) => params.append("filtros", `${f.campo}:${f.valor}`));

      const r = await axios.get(
        `${API}/usuarios-contribuyentes/export?${params.toString()}`,
        { ...auth, responseType: "blob" },
      );

      // Extraer filename de Content-Disposition (fallback si no viene)
      const cd = r.headers["content-disposition"] || r.headers["Content-Disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `usuarios_contribuyentes.${formato}`;

      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado como ${formato.toUpperCase()}: ${filename}`);
    } catch (e) {
      // Blob → intentar parsear JSON de error si aplica
      let detail = "Error al exportar";
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || detail; } catch { detail = text || detail; }
        } else if (e.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch { /* noop */ }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  // ============ Acciones ============
  const openSuspend = (u) => { setSelected(u); setMotivo(""); setDlgSuspend(true); };
  const openReactivate = async (u) => {
    if (!window.confirm(`¿Reactivar el acceso de ${u.email}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/usuarios-contribuyentes/${u.id}/suspend`, { accion: "reactivate" }, auth);
      toast.success(r.data.message || "Usuario reactivado");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const submitSuspend = async () => {
    if (motivo.trim().length < 5) { toast.error("El motivo debe tener al menos 5 caracteres"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/usuarios-contribuyentes/${selected.id}/suspend`,
        { accion: "suspend", motivo: motivo.trim() }, auth);
      toast.success(r.data.message || "Usuario suspendido");
      setDlgSuspend(false); setSelected(null); setMotivo("");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const openReset = (u) => { setSelected(u); setResetInfo(null); setDlgReset(true); };
  const openResetTemp = async (u) => {
    if (!window.confirm(
      `¿Reset directo de la clave para ${u.nombre_completo || u.email}?\n\n` +
      `La clave será: Palmaplus\n\n` +
      `El contribuyente podrá ingresar con esa clave, pero se le exigirá cambiarla y aceptar los Términos y Condiciones al primer acceso.\n\n` +
      `NO se envía link ni email — usted es responsable de comunicar la clave al contribuyente.`
    )) return;
    try {
      const r = await axios.post(`${API}/usuarios-contribuyentes/${u.id}/reset-to-temporal`, {}, auth);
      toast.success(
        `Clave restablecida a "${r.data?.clave_temporal || "Palmaplus"}". El contribuyente deberá cambiarla y aceptar los T&C al primer acceso.`,
        { duration: 10000 }
      );
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al restablecer la clave");
    }
  };
  const submitReset = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/usuarios-contribuyentes/${selected.id}/reset-password`, {}, auth);
      setResetInfo(r.data);
      if (r.data.email_status === "sin_api_key") {
        toast.warning("Email no configurado. Comparta el link manualmente.");
      } else if (r.data.email_status === "enviado") {
        toast.success(`Enlace enviado a ${r.data.email_destino}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const copyResetUrl = async () => {
    try {
      await navigator.clipboard.writeText(resetInfo.reset_url);
      toast.success("Enlace copiado al portapapeles");
    } catch { toast.error("No se pudo copiar"); }
  };

  const openDelete = (u) => { setSelected(u); setMotivo(""); setDlgDelete(true); };
  const submitDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/usuarios-contribuyentes/${selected.id}`, {
        ...auth,
        data: { motivo: motivo.trim() || null },
      });
      toast.success("Acceso al portal eliminado. La ficha fiscal permanece.");
      setDlgDelete(false); setSelected(null); setMotivo("");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  if (!canView) {
    return <div className="p-8 text-center text-red-600" data-testid="uc-no-access">No tiene permisos para ver este módulo.</div>;
  }

  return (
    <div className="space-y-6" data-testid="usuarios-contribuyentes-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
            <UserCog className="w-8 h-8 text-orange-500" /> Usuarios Contribuyentes
          </h1>
          <p className="text-green-700/70 mt-1">
            Gestione el <strong>acceso al portal</strong> de los contribuyentes. Estas acciones NO afectan la ficha fiscal.
          </p>
        </div>
        {/* Botón de exportación con los filtros activos */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={exporting || total === 0}
              className="bg-green-700 hover:bg-green-800 text-white shadow-sm"
              data-testid="uc-export-btn"
              title={total === 0 ? "Sin registros para exportar" : "Exportar el listado filtrado"}
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exporting ? "Exportando…" : `Exportar (${total})`}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Elegir formato</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid="uc-export-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-700" /> Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="uc-export-csv">
              <FileText className="w-4 h-4 mr-2 text-slate-700" /> CSV (.csv)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport("json")} data-testid="uc-export-json">
              <FileJson className="w-4 h-4 mr-2 text-blue-700" /> JSON (.json)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="uc-export-pdf">
              <FileType2 className="w-4 h-4 mr-2 text-red-700" /> PDF (.pdf)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="usuarios" data-testid="tab-usuarios"><Users2 className="w-4 h-4 mr-1.5" />Usuarios</TabsTrigger>
          {canViewLog && <TabsTrigger value="historial" data-testid="tab-historial"><History className="w-4 h-4 mr-1.5" />Historial de acciones</TabsTrigger>}
        </TabsList>

        {/* =============== TAB USUARIOS =============== */}
        <TabsContent value="usuarios" className="space-y-3">
          <Card className="p-4 border-green-100 space-y-3">
            {/* Fila 1: búsqueda global + estado */}
            <form onSubmit={onSearch} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs">Buscar (todos los campos)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="uc-search-input" className="pl-9" placeholder="Email, nombre, RIF/cédula, razón social o teléfono…"
                         value={q} onChange={e => setQ(e.target.value)} />
                </div>
              </div>
              <div className="w-52">
                <Label className="text-xs">Estado</Label>
                <Select value={estado} onValueChange={(v) => { setEstado(v); setSkip(0); }}>
                  <SelectTrigger data-testid="uc-estado-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="suspendido">Suspendido</SelectItem>
                    <SelectItem value="pendiente">Pre-aprobado</SelectItem>
                    <SelectItem value="rechazado">Rechazado</SelectItem>
                    <SelectItem value="sin_acceso">Sin acceso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="uc-search-btn" type="submit" className="bg-green-700 hover:bg-green-800 text-white h-9">Buscar</Button>
              <Button type="button" variant="ghost" onClick={clearAllFiltros} data-testid="uc-clear-all"
                      title="Limpiar todos los filtros">
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </form>

            {/* Fila 2: multi-filtro por campo */}
            <div className="pt-3 border-t border-green-50">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-green-700" />
                <span className="text-sm font-medium text-green-900">Filtros por campo</span>
                <span className="text-xs text-slate-500">(se combinan con AND)</span>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-44">
                  <Label className="text-xs">Campo</Label>
                  <Select value={draftCampo} onValueChange={setDraftCampo}>
                    <SelectTrigger data-testid="uc-filter-campo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CAMPOS_FILTRO.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs">Valor</Label>
                  <Input
                    data-testid="uc-filter-valor"
                    value={draftValor}
                    onChange={(e) => setDraftValor(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFiltro(); } }}
                    placeholder={(CAMPOS_FILTRO.find(c => c.key === draftCampo) || {}).placeholder || "Escriba el valor…"}
                  />
                </div>
                <Button
                  type="button"
                  onClick={addFiltro}
                  disabled={!draftValor.trim()}
                  className="h-9 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="uc-filter-add"
                >
                  <Plus className="w-4 h-4 mr-1" /> Agregar filtro
                </Button>
              </div>
              {/* Chips de filtros activos */}
              {filtros.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="uc-filter-chips">
                  {filtros.map((f, idx) => (
                    <span
                      key={`${f.campo}-${f.valor}-${idx}`}
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-green-100 text-green-900 text-xs font-medium border border-green-200"
                      data-testid={`uc-filter-chip-${f.campo}`}
                    >
                      <span className="text-green-700 font-semibold">{CAMPO_LABELS[f.campo]}:</span>
                      <span className="truncate max-w-[180px]">{f.valor}</span>
                      <button
                        type="button"
                        onClick={() => removeFiltro(idx)}
                        className="ml-1 rounded-full hover:bg-green-200 p-0.5 transition-colors"
                        aria-label={`Quitar filtro ${CAMPO_LABELS[f.campo]}`}
                        data-testid={`uc-filter-chip-remove-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setFiltros([]); setSkip(0); }}
                    className="text-xs text-slate-500 hover:text-red-600 underline ml-1"
                    data-testid="uc-filter-clear-all"
                  >
                    Quitar todos
                  </button>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-0 border-green-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900">
                  <tr>
                    <th className="text-left p-3">RIF / Cédula</th>
                    <th className="text-left p-3">Nombre / Razón social</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Teléfono</th>
                    <th className="text-center p-3">Estado</th>
                    <th className="text-left p-3">Última conexión</th>
                    <th className="text-right p-3 w-24">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="p-10 text-center text-slate-500">Cargando…</td></tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr><td colSpan={7} className="p-10 text-center text-slate-500">Sin resultados</td></tr>
                  )}
                  {!loading && items.map((u) => {
                    const e = ESTADO_BADGE[u.estado] || ESTADO_BADGE.inactivo;
                    return (
                      <tr key={u.id} className="border-b border-green-50 hover:bg-green-50/30" data-testid={`uc-row-${u.id}`}>
                        <td className="p-3 font-mono text-xs">{u.tipo_documento || ""}{u.documento || "—"}</td>
                        <td className="p-3">
                          <div className="font-medium text-green-900">{u.razon_social || `${u.nombre || ""} ${u.apellido || ""}`.trim() || "—"}</div>
                          {u.clase && <div className="text-[10px] uppercase tracking-wider text-slate-400">{u.clase}</div>}
                        </td>
                        <td className="p-3">{u.email}</td>
                        <td className="p-3">{u.telefono || "-"}</td>
                        <td className="p-3 text-center">
                          <Badge className={`${e.cls} hover:${e.cls}`} data-testid={`uc-estado-${u.id}`}>{e.label}</Badge>
                          {u.estado === "suspendido" && u.suspend_motivo && (
                            <div className="text-[10px] text-red-600 mt-1 max-w-[220px] mx-auto italic truncate" title={u.suspend_motivo}>
                              {u.suspend_motivo}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-slate-600">{fmtDate(u.last_login)}</td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`uc-actions-${u.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {u.estado === "suspendido" ? (
                                canSuspend && (
                                  <DropdownMenuItem onClick={() => openReactivate(u)} data-testid={`uc-reactivate-${u.id}`}>
                                    <ShieldCheck className="w-4 h-4 mr-2 text-green-700" /> Reactivar acceso
                                  </DropdownMenuItem>
                                )
                              ) : (
                                canSuspend && u.estado !== "sin_acceso" && (
                                  <DropdownMenuItem onClick={() => openSuspend(u)} data-testid={`uc-suspend-${u.id}`}>
                                    <Ban className="w-4 h-4 mr-2 text-red-600" /> Suspender acceso
                                  </DropdownMenuItem>
                                )
                              )}
                              {canReset && u.estado !== "sin_acceso" && (
                                <DropdownMenuItem onClick={() => openReset(u)} data-testid={`uc-reset-${u.id}`}>
                                  <KeyRound className="w-4 h-4 mr-2 text-orange-600" /> Enviar link de reseteo
                                </DropdownMenuItem>
                              )}
                              {canReset && u.estado !== "sin_acceso" && (
                                <DropdownMenuItem onClick={() => openResetTemp(u)} data-testid={`uc-reset-temp-${u.id}`}>
                                  <KeyRound className="w-4 h-4 mr-2 text-amber-700" /> Reset a clave &quot;Palmaplus&quot;
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => openDelete(u)} className="text-red-700" data-testid={`uc-delete-${u.id}`}>
                                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar acceso al portal
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between p-3 border-t border-green-50 bg-green-50/40 text-sm">
              <div className="text-slate-600">
                {total > 0 ? <>Mostrando <strong>{skip + 1}</strong>–<strong>{Math.min(skip + limit, total)}</strong> de <strong>{total}</strong></> : "0 resultados"}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="uc-page-prev">Anterior</Button>
                <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="uc-page-next">Siguiente</Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* =============== TAB HISTORIAL =============== */}
        {canViewLog && (
          <TabsContent value="historial" className="space-y-3">
            <Card className="p-4 border-green-100">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-56">
                  <Label className="text-xs">Filtrar por acción</Label>
                  <Select value={logFiltroAccion} onValueChange={setLogFiltroAccion}>
                    <SelectTrigger data-testid="uc-log-accion-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="suspend">Suspensiones</SelectItem>
                      <SelectItem value="reactivate">Reactivaciones</SelectItem>
                      <SelectItem value="reset_password">Reset password (link)</SelectItem>
                      <SelectItem value="reset_password_temporal">Reset a clave &quot;Palmaplus&quot;</SelectItem>
                      <SelectItem value="delete">Eliminaciones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={loadLog} data-testid="uc-log-refresh"><RefreshCcw className="w-4 h-4 mr-1.5" /> Refrescar</Button>
              </div>
            </Card>

            <Card className="p-0 border-green-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-green-50 text-green-900">
                    <tr>
                      <th className="text-left p-3">Fecha</th>
                      <th className="text-left p-3">Acción</th>
                      <th className="text-left p-3">Usuario afectado</th>
                      <th className="text-left p-3">Contribuyente</th>
                      <th className="text-left p-3">Motivo</th>
                      <th className="text-left p-3">Ejecutado por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logLoading && <tr><td colSpan={6} className="p-10 text-center text-slate-500">Cargando…</td></tr>}
                    {!logLoading && logItems.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">Sin registros</td></tr>}
                    {!logLoading && logItems.map((l) => {
                      const meta = ACCION_META[l.accion] || { label: l.accion, icon: History, cls: "text-slate-700 bg-slate-100" };
                      const Icon = meta.icon;
                      return (
                        <tr key={l.id} className="border-b border-green-50" data-testid={`uc-log-row-${l.id}`}>
                          <td className="p-3 text-slate-600 whitespace-nowrap">{fmtDate(l.fecha_ejecucion)}</td>
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
                              <Icon className="w-3.5 h-3.5" /> {meta.label}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="text-green-900 font-medium">{l.usuario_nombre || "—"}</div>
                            <div className="text-xs text-slate-500">{l.usuario_email || "—"}</div>
                          </td>
                          <td className="p-3">
                            <div className="font-mono text-xs">{l.contribuyente_documento || "—"}</div>
                            <div className="text-xs text-slate-600">{l.contribuyente_nombre || "—"}</div>
                          </td>
                          <td className="p-3 max-w-[240px]">
                            <div className="text-slate-700 text-xs italic truncate" title={l.motivo || ""}>{l.motivo || "—"}</div>
                          </td>
                          <td className="p-3">
                            <div className="text-slate-800 text-xs">{l.ejecutado_por_nombre || "—"}</div>
                            <div className="text-[11px] text-slate-500">{l.ejecutado_por_email || ""}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ============ Diálogo: Suspender ============ */}
      <Dialog open={dlgSuspend} onOpenChange={(o) => { if (!o) { setDlgSuspend(false); setSelected(null); setMotivo(""); } }}>
        <DialogContent data-testid="uc-suspend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="w-5 h-5" /> Suspender acceso al portal</DialogTitle>
            <DialogDescription>
              El contribuyente <strong>{selected?.email}</strong> no podrá iniciar sesión hasta que reactive su acceso. Su ficha fiscal, planillas y pagos NO se ven afectados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo de la suspensión* (mínimo 5 caracteres)</Label>
            <Textarea data-testid="uc-suspend-motivo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
                      placeholder="Ej: Sospecha de fraude; múltiples reportes de pago inválidos…" />
            <p className="text-xs text-slate-500">Este motivo se registra en el historial y se envía por email al contribuyente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgSuspend(false)}>Cancelar</Button>
            <Button data-testid="uc-suspend-confirm" onClick={submitSuspend} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">
              <Ban className="w-4 h-4 mr-1.5" /> {busy ? "Suspendiendo…" : "Suspender"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Diálogo: Reset Password ============ */}
      <Dialog open={dlgReset} onOpenChange={(o) => { if (!o) { setDlgReset(false); setSelected(null); setResetInfo(null); } }}>
        <DialogContent data-testid="uc-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700"><KeyRound className="w-5 h-5" /> Enviar link de restablecimiento</DialogTitle>
            <DialogDescription>
              Se enviará un enlace válido por <strong>24 horas</strong> al correo <strong>{selected?.email}</strong> para que el contribuyente defina su nueva contraseña.
            </DialogDescription>
          </DialogHeader>

          {!resetInfo ? (
            <div className="rounded-lg bg-orange-50 border border-orange-100 p-3 text-sm text-orange-900">
              <p><strong>Importante:</strong> nunca se comparte la nueva contraseña con el funcionario. El contribuyente la crea desde el link.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {resetInfo.email_status === "sin_api_key" ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <strong>Email no configurado (RESEND_API_KEY vacía).</strong>
                    <p className="mt-1">Copie el siguiente enlace y compártalo manualmente con el contribuyente:</p>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-green-50 border border-green-100 p-3 text-sm text-green-900">
                  ✅ Enlace enviado exitosamente a <strong>{resetInfo.email_destino}</strong>. Vence en {resetInfo.expires_hours}h.
                </div>
              )}
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center gap-2">
                <div className="flex-1 text-xs font-mono break-all text-slate-700" data-testid="uc-reset-url">{resetInfo.reset_url}</div>
                <Button size="sm" variant="outline" onClick={copyResetUrl} data-testid="uc-copy-reset-url">
                  <Copy className="w-4 h-4 mr-1" /> Copiar
                </Button>
                <a href={resetInfo.reset_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline"><ExternalLink className="w-4 h-4" /></Button>
                </a>
              </div>
              {resetInfo.whatsapp_url && (
                <a href={resetInfo.whatsapp_url} target="_blank" rel="noopener noreferrer" className="block" data-testid="uc-wa-share">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1EBE5C] text-white font-semibold">
                    <MessageCircle className="w-4 h-4 mr-1.5" />
                    {resetInfo.whatsapp_phone
                      ? `Enviar por WhatsApp a +${resetInfo.whatsapp_phone}`
                      : "Compartir por WhatsApp (elegir contacto)"}
                  </Button>
                </a>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlgReset(false); setResetInfo(null); }}>Cerrar</Button>
            {!resetInfo && (
              <Button data-testid="uc-reset-send" onClick={submitReset} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
                <KeyRound className="w-4 h-4 mr-1.5" /> {busy ? "Enviando…" : "Enviar enlace"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Diálogo: Eliminar acceso ============ */}
      <Dialog open={dlgDelete} onOpenChange={(o) => { if (!o) { setDlgDelete(false); setSelected(null); setMotivo(""); } }}>
        <DialogContent data-testid="uc-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="w-5 h-5" /> Eliminar acceso al portal</DialogTitle>
            <DialogDescription>
              Se eliminará el usuario del portal para <strong>{selected?.email}</strong>. La <strong>ficha fiscal del contribuyente NO se borra</strong>, pero el estado se marcará como <em>sin acceso</em> y no podrá auto-registrarse sin aprobación del funcionario.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>Esta acción es <strong>irreversible</strong>. Puede volver a habilitar el acceso desde la ficha fiscal del contribuyente contactándolo.</div>
          </div>
          <div className="space-y-2">
            <Label>Motivo (opcional, se registra en el historial)</Label>
            <Textarea data-testid="uc-delete-motivo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} placeholder="Ej: Cuenta duplicada; solicitud del contribuyente…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgDelete(false)}>Cancelar</Button>
            <Button data-testid="uc-delete-confirm" onClick={submitDelete} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-1.5" /> {busy ? "Eliminando…" : "Eliminar acceso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
